import React, { useEffect, useMemo, useState } from 'react'
import { PGA_COM_HOST } from 'env'
import { get } from 'lodash'
import { useQuery } from '@apollo/client'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory, useRouteMatch } from 'react-router-dom'

import Box from '@material-ui/core/Box'
import GolfCourseIcon from 'components/material-symbol/icons/GolfCourse'
import LinearProgress from '@material-ui/core/LinearProgress'

import { useAuth } from 'lib/auth'
import { formatGreeting } from 'utils/greeting'
import { gtmEvent } from 'lib/gtm'
import { parseData, hasAtLeastOneCoach } from './utils'
import { GetCoachesQuery } from './query'
import EmptyCoachPage from 'components/empty-page-state'
import ViewingGolfForSelector from './viewing-golf-for-selector'
import SelectCoachModal from './select-coach-modal'
import Layout from './Layout'
import { clubDashboardPageInset } from './styles'
import ClubDashboardHeader from './club-dashboard-header'

const COACH_SEARCH_URL = `${PGA_COM_HOST}/coaches/search`

const useHeaderStyles = makeStyles(theme => ({
  page: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  pageInset: clubDashboardPageInset(theme),
}))

export const MyCoach = ({ students, lessonCreditBalances }) => {
  const [selectedStudent, setSelectedStudent] = useState(
    get(students, ['0', 'id']),
  )
  const [bookModalOpen, setBookModalOpen] = useState(false)
  const headerClasses = useHeaderStyles()
  const history = useHistory()
  const { path: clubPath } = useRouteMatch()
  const { user } = useAuth()
  const firstName = user?.firstName || user?.name?.split(' ')?.[0] || 'Golfer'
  const greetingTitle = formatGreeting(firstName)
  const selectedStudentData = useMemo(
    () => students.find(({ id }) => id === selectedStudent),
    [selectedStudent, students],
  )
  const coaches = selectedStudentData?.coaches ?? []

  useEffect(() => {
    setBookModalOpen(false)
  }, [selectedStudent])

  const navigateToCoachSchedule = coach => {
    history.push(`/coach/${coach.coachSlug || coach.externalId}/schedule`)
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
  }

  const handleBookClick = () => {
    if (!coaches.length) {
      window.location.href = COACH_SEARCH_URL
      return
    }

    if (coaches.length === 1) {
      navigateToCoachSchedule(coaches[0])
      return
    }

    setBookModalOpen(true)
  }

  const handleCoachSelect = coach => {
    setBookModalOpen(false)
    navigateToCoachSchedule(coach)
  }

  return (
    <Box className={headerClasses.page}>
      <Box className={headerClasses.pageInset}>
        <ClubDashboardHeader
          greetingTitle={greetingTitle}
          onBookClick={handleBookClick}
          messagesPath={`${clubPath}/messages`}
        />
        <SelectCoachModal
          open={bookModalOpen}
          onClose={() => setBookModalOpen(false)}
          coaches={coaches}
          onSelectCoach={handleCoachSelect}
        />
        <ViewingGolfForSelector
          students={students}
          selectedStudent={selectedStudent}
          setSelectedStudent={setSelectedStudent}
        />
        {hasAtLeastOneCoach(students) ? (
          <Layout
            students={students}
            selectedStudent={selectedStudent}
            setSelectedStudent={setSelectedStudent}
            lessonCreditBalances={lessonCreditBalances}
            onBookClick={handleBookClick}
          />
        ) : (
          <EmptyCoachPage
            actionFn={() => {
              window.location.href = `${PGA_COM_HOST}/coaches/search`
            }}
            IconComp={() => <GolfCourseIcon fontSize="large" />}
            title="My Coach"
            caption="Find your next coach today."
            buttonText="Search Coaches"
          />
        )}
      </Box>
    </Box>
  )
}

export default () => {
  const { data, loading, error } = useQuery(GetCoachesQuery)
  if (loading) return <LinearProgress color="secondary" />
  if (error) return <Box>ERROR</Box>

  const representatives = get(data, 'currentUser.representatives')
  const lessonCreditBalances = get(data, 'currentUser.attributes.lessonPacks')

  return (
    <MyCoach
      students={parseData(representatives)}
      lessonCreditBalances={lessonCreditBalances}
    />
  )
}
